// Copyright (c) Docugami, Inc. All rights reserved.

import Worker from './worker'
import * as Comlink from 'comlink';
import { DgOnSelectionEventArgs } from './DgOnSelectionEventArgs';
import { getDocugamiIdFromRootElement } from './DgParagraphMetadataUtil';
import { DgEvent } from './DgEvent';
import { DgOnFieldValueChange } from './DgOnFieldValueChange';
import { DOMParser } from '@xmldom/xmldom'
import { tokenBreakingCharactersForWord } from './DgTokenBreakingCharacters';
import { v4 as uuidv4 } from 'uuid';
import { BucketTimer } from '../../../../../utils/bucketTimer';
import { IDgWordCanvasProxyDirectProxies } from './worker/worker';
import { DgKnownChunk } from "./DgParagraphMetadata";
import { Logger, LoggerCategory } from 'utils/Logger';
import { DgWordCanvas, tokenizeString } from './DgWordCanvas';
import { removeDeletedSpanIdsFromDocument } from './DgSelectAndDeleteContent';
import { transformAbstractXmlToWml } from './DgTransformAbstractXmlToWml';
import { produceAbstractXml } from './DgAbstractXmlProducer';
import { transformWmlToDgml } from './DgTransformWmlToDgml';

/*eslint-disable */

// Note: the following const have eslint-disable because they form the whole of the Open-Xml-Sdk for JavaScript API.
// Most modules will use only a subset of the API, but disabling portions of the API leads to friction when writing
// code.  Disabling eslint for these variables removes the error indicating that a variable is assigned a value but
// never used.  If we ever re-write these modules in TypeScript, then the public API will change significantly.

const Enumerable = require("../../../../../utils/linq");
const Ltxml = require('../../../../../utils/ltxml.js');
Ltxml.DOMParser = DOMParser

const openXml = require('../../../../../utils/openxml');

const XAttribute = Ltxml.XAttribute;
const XCData = Ltxml.XCData;
const XComment = Ltxml.XComment;
const XContainer = Ltxml.XContainer;
const XDeclaration = Ltxml.XDeclaration;
const XDocument = Ltxml.XDocument;
const XElement = Ltxml.XElement;
const XName = Ltxml.XName;
const XNamespace = Ltxml.XNamespace;
const XNode = Ltxml.XNode;
const XObject = Ltxml.XObject;
const XProcessingInstruction = Ltxml.XProcessingInstruction;
const XText = Ltxml.XText;
const XEntity = Ltxml.XEntity;
const cast = Ltxml.cast;
const castInt = Ltxml.castInt;

const W = openXml.W;
const W14 = openXml.W14;
const W15 = openXml.W15;
const NN = openXml.NoNamespace;
const wNs = openXml.wNs;
/*eslint-enable */

let eatEvents = false;

let contentChangedTimeoutToken: any|null = null;
const DOCUMENT_CHANGED_DEBOUNCE_MS = 250;
let lastDocumentContents: any = null;

async function bucketTimer(num: number, desc: string): Promise<boolean> {
    // helpful for diagnostics when things go wrong in initialization
    // window.localStorage.setItem("DOCUGAMI_LAST_BUCKET", desc);
    BucketTimer.bucket(num, desc);
    return true;
}

async function getBodyOoxml(): Promise<string> {
    let OOXML: any = null;
    await Word.run(async context => {
        OOXML = context.document.body.getOoxml();
        await context.sync();
    });
    return OOXML.value;
}

async function replaceBodyOoxml(ooxml: string): Promise<boolean> {
    await Word.run(async context => {
        context.document.body.insertOoxml(ooxml, Word.InsertLocation.replace);
        await context.sync();
    });

    return true;
}

async function selectOrDeleteContentByParaIdxsAndTokens(indexes: number[], setIpAtStart: boolean, selectTokens: string | null, deleteAfterSelect: boolean, deletedChunkSpanIds: string[]): Promise<boolean>
{
    let idxsLenString = "";
    let idxsString = "";
    if (!!indexes)
    {
        idxsLenString = ` indexes.length ${indexes.length}`;
        const idxs = indexes.map((i: number) => i.toString()).join('|');
        idxsString = ` indexes >>${idxs}<<`;
    }
    const setIpAtStartString = ` setIpAtStart ${setIpAtStart}`;
    let selectTokensString = "";
    if (!!selectTokens)
    {
        selectTokensString = ` selectTokens >>${selectTokens}<<`;
    }
    const deleteAfterSelectString = ` deleteAfterSelect ${deleteAfterSelect}`;
    let deletedChunkSpanIdsString = "";
    if (!!deletedChunkSpanIds)
    {
        const csi = deletedChunkSpanIds.join('|');
        deletedChunkSpanIdsString = ` deletedChunkSpanIds.length ${deletedChunkSpanIds.length} deletedChunkSpanIds >>${csi}<<`;
    }
    Logger.LogTrace(LoggerCategory.AssistedAuthoring, `selectOrDeleteContentByParaIdxsAndTokens entry${idxsLenString}${idxsString}${setIpAtStartString}${selectTokensString}${deleteAfterSelectString}${deletedChunkSpanIdsString}`);

    await Word.run(async context => {

        if (indexes === null || indexes.length === 0) {
            Logger.LogError(LoggerCategory.AssistedAuthoring, "selectOrDeleteContentByParaIdxsAndTokens - warning - indexes array is empty");
            return false;
        }

        const paragraphs = context.document.body.paragraphs;
        paragraphs.load("$none");
        await context.sync();

        if (setIpAtStart) {
            const firstParagraph = paragraphs.items[indexes[0]] as Word.Paragraph;
            const insertionPoint = firstParagraph.getRange(Word.RangeLocation.start);
            Logger.LogDebug(LoggerCategory.AssistedAuthoring, `selectOrDeleteContentByParaIdxsAndTokens selecting IP on canvas`);
            insertionPoint.select(Word.SelectionMode.select);
            Logger.LogDebug(LoggerCategory.AssistedAuthoring, `==================================> SUCCESS <================================== ${DgWordCanvas.actionCount++}`);
        }
        else {
            let rangeToSelectOrDelete: Word.Range | null = null;

            if (selectTokens !== null) {
                // indexes.length might be 1 or >1
                if (indexes.length === 1)
                {
                    const onlyParagraph = paragraphs.items[indexes[0]] as Word.Paragraph; // there is only one
                    context.load(onlyParagraph, 'text');
                    await context.sync();

                    const onlyParagraphText: string = onlyParagraph.text;
                    const onlyParagraphTokens: string[] = tokenizeString(onlyParagraphText);

                    let onlyParagraphRange: Word.Range = onlyParagraph.getRange(Word.RangeLocation.whole);
                    rangeToSelectOrDelete = await getRangeBasedOnTokens(context, onlyParagraphRange, selectTokens, true, onlyParagraphTokens.length);

                    if (!rangeToSelectOrDelete || rangeToSelectOrDelete.isNullObject) {
                        // If we could not find a range, then select/delete the entire paragraph.
                        // There is a chance that this will provide bad behavior - if the intent is to delete a few tokens in the middle of a paragraph,
                        // and if we did not find those tokens, then this would delete the entire paragraph.  However, in two other cases, this will
                        // provide better behavior.  If we were finding a large part of the paragraph, or if we were finding the entire paragraph, but
                        // due to editing or strange behavior in Word, could not find those tokens, then the behavior of this code would be better.
                        // When we revamp this code to address the issues associated with continual editing of the document, and updating of recommendations,
                        // this code will be completely re-written, I do believe.
                        rangeToSelectOrDelete = onlyParagraphRange;
                    }
                }
                else { // if (indexes.length >= 2)
                    const paragraphIndexForFirstRange = indexes[0];
                    let paragraphIndexForLastRange = indexes[indexes.length - 1];
                    if (paragraphIndexForLastRange >= paragraphs.items.length) {
                        paragraphIndexForLastRange = paragraphs.items.length - 1;
                    }
                    const paragraphForFirstRange = paragraphs.items[paragraphIndexForFirstRange] as Word.Paragraph;
                    const paragraphForLastRange = paragraphs.items[paragraphIndexForLastRange] as Word.Paragraph;
                    context.load(paragraphForFirstRange, 'text');
                    context.load(paragraphForLastRange, 'text');

                    await context.sync();

                    const firstParagraphText: string = paragraphForFirstRange.text;
                    const firstParagraphTokens: string[] = tokenizeString(firstParagraphText);

                    const lastParagraphText: string = paragraphForLastRange.text;
                    const lastParagraphTokens: string[] = tokenizeString(lastParagraphText);

                    let firstParagraphRange: Word.Range = paragraphForFirstRange.getRange(Word.RangeLocation.whole);
                    let firstParagraphRangeToSelectOrDelete = await getRangeBasedOnTokens(context, firstParagraphRange, selectTokens, true, firstParagraphTokens.length);
                    if (!firstParagraphRangeToSelectOrDelete || firstParagraphRangeToSelectOrDelete.isNullObject) {
                        // This is similar to the algorithm when there is only one paragraph - see comment above.
                        firstParagraphRangeToSelectOrDelete = firstParagraphRange;
                    }

                    let lastParagraphRange: Word.Range = paragraphForLastRange.getRange(Word.RangeLocation.whole);
                    let lastParagraphRangeToSelectOrDelete = await getRangeBasedOnTokens(context, lastParagraphRange, selectTokens, false, lastParagraphTokens.length);
                    if (!lastParagraphRangeToSelectOrDelete || lastParagraphRangeToSelectOrDelete.isNullObject) {
                        // This is similar to the algorithm when there is only one paragraph - see comment above.
                        lastParagraphRangeToSelectOrDelete = lastParagraphRange;
                    }

                    rangeToSelectOrDelete = firstParagraphRangeToSelectOrDelete.expandTo(lastParagraphRangeToSelectOrDelete);
                }

                let removeDeletedSpanIdFromParagraphs: boolean = false;
                if (rangeToSelectOrDelete !== null) {
                    if (deleteAfterSelect) {
                        Logger.LogDebug(LoggerCategory.AssistedAuthoring, `selectOrDeleteContentByParaIdxsAndTokens deleting range`);
                        rangeToSelectOrDelete.delete();
                        Logger.LogDebug(LoggerCategory.AssistedAuthoring, `==================================> SUCCESS <================================== ${DgWordCanvas.actionCount++}`);
                        removeDeletedSpanIdFromParagraphs = true;
                    }
                    else {
                        Logger.LogDebug(LoggerCategory.AssistedAuthoring, `selectOrDeleteContentByParaIdxsAndTokens selecting range`);
                        rangeToSelectOrDelete.select(Word.SelectionMode.select);
                        Logger.LogDebug(LoggerCategory.AssistedAuthoring, `==================================> SUCCESS <================================== ${DgWordCanvas.actionCount++}`);
                    }
                }

                if (removeDeletedSpanIdFromParagraphs) {
                    let OOXML: any = context.document.body.getOoxml();
                    await context.sync();

                    const root = XElement.parse(OOXML.value);

                    const needToReplace = removeDeletedSpanIdsFromDocument(root, deletedChunkSpanIds);

                    if (needToReplace) {
                        context.document.body.insertOoxml(root.toString(), Word.InsertLocation.replace);
                    }
                }
            }
            else {
                const firstParagraph = paragraphs.items[indexes[0]] as Word.Paragraph;
                let mergedRange: Word.Range = firstParagraph.getRange(Word.RangeLocation.whole);
                Enumerable.from(indexes).skip(1).forEach((idx: number) => {
                    const nextParagraph = paragraphs.items[idx] as Word.Paragraph;
                    const nextRange: Word.Range = nextParagraph.getRange(Word.RangeLocation.end);
                    mergedRange = mergedRange.expandTo(nextRange);
                });
                if (deleteAfterSelect) {
                    Logger.LogDebug(LoggerCategory.AssistedAuthoring, `selectOrDeleteContentByParaIdxsAndTokens deleting merged range`);
                    mergedRange.delete();
                    Logger.LogDebug(LoggerCategory.AssistedAuthoring, `==================================> SUCCESS <================================== ${DgWordCanvas.actionCount++}`);
                }
                else {
                    Logger.LogDebug(LoggerCategory.AssistedAuthoring, `selectOrDeleteContentByParaIdxsAndTokens selecting merged range`);
                    mergedRange.select(Word.SelectionMode.select);
                    Logger.LogDebug(LoggerCategory.AssistedAuthoring, `==================================> SUCCESS <================================== ${DgWordCanvas.actionCount++}`);
                }
            }
        }

        await context.sync();
    });

    return true;
}

async function getRangeBasedOnTokens(context: Word.RequestContext, paragraphRange: Word.Range, selectTokens: string, selectAtBeginningElseEnd: boolean, maxTokens: number): Promise<Word.Range | null>
{
    Logger.LogTrace(LoggerCategory.AssistedAuthoring, `getRangeBasedOnTokens selectTokens >>${selectTokens}<<`);

    const splitTokens = selectTokens.split(' ');
    const splitTokensWithoutPunctuation: string[] = Enumerable.from(splitTokens)
        .where((n: string) => {
            const isPunct = isPunctuation(n);
            const isQuoteOrParen = (n === '(' || n === ')' || n === '“' || n === '”');
            return !(isPunct || isQuoteOrParen);
        })
        .toArray();

    for (let i = 1; i <= 2; ++i) {
        let searchString = createSearchString(splitTokens, splitTokensWithoutPunctuation, i, selectAtBeginningElseEnd, maxTokens);
        const foundRanges: Word.RangeCollection = paragraphRange.search(searchString, {
            matchCase: false,
            matchWildcards: true
        });
        context.load(foundRanges, 'text');
        await context.sync();

        let mergedRange: Word.Range = foundRanges.getFirstOrNullObject();
        context.load(mergedRange, 'text');
        const endParagraphRange = paragraphRange.getRange(Word.RangeLocation.end);
        await context.sync();

        mergedRange = mergedRange.expandTo(endParagraphRange);
        await context.sync();

        if (!mergedRange.isNullObject) {
            return mergedRange;
        }
    }
    Logger.LogDebug(LoggerCategory.AssistedAuthoring, `getRangeBasedOnTokens returning null`);
    return null;
}

function isPunctuation(token: string): boolean
{
    return token === "." || token === ",";
}

function createSearchString(splitTokens: string[], splitTokensWithoutPunctuation: string[], iteration: number, selectAtBeginningElseEnd: boolean, maxTokens: number): string
{
    const MAXIMUM_PARTIAL_SEARCH_STRING_LENGTH = 8;
    const maxToUse = Math.min(MAXIMUM_PARTIAL_SEARCH_STRING_LENGTH, maxTokens);

    let partialSearchLength = Math.min(splitTokensWithoutPunctuation.length, maxToUse);
    if (iteration === 2) {
        partialSearchLength = Math.min(splitTokensWithoutPunctuation.length, maxToUse / 2);
    }
    if (partialSearchLength < 1) {
        partialSearchLength = 1;
    }
    if (selectAtBeginningElseEnd) {
        const searchStringAtBeginning = Enumerable.from(splitTokensWithoutPunctuation).take(partialSearchLength).toArray().join("*");
        return searchStringAtBeginning;
    }
    else {
        const searchStringAtEnd = Enumerable.from(splitTokensWithoutPunctuation).reverse().take(partialSearchLength).reverse().toArray().join("*");
        return searchStringAtEnd;
    }
}

async function getWordRangesForParas(paraIdxs: number[], context: Word.RequestContext): Promise<Array<Word.RangeCollection | null>>
{
    const paragraphs = context.document.body.paragraphs
    paragraphs.load("$none");
    await context.sync();

    const wordRangesForParas: Array<Word.RangeCollection | null> = [];
    for (const paraIdx of paraIdxs) {
        if (paraIdx === -1 || paraIdx >= paragraphs.items.length) {
            wordRangesForParas.push(null);
            continue;
        }

        const paragraph = paragraphs.items[paraIdx];
        context.load(paragraph);
        const ranges = paragraph.getTextRanges(tokenBreakingCharactersForWord, true);
        context.load(ranges);
        wordRangesForParas.push(ranges);
    }

    await context.sync();
    return wordRangesForParas;
}

async function getTokenizedParagraphs(paraIdxs: number[]): Promise<string[][]> {
    const paraTokens: string[][] = [];

    await Word.run(async context => {
        const wordRangesForParas = await getWordRangesForParas(paraIdxs, context);

        for (let i = 0; i < wordRangesForParas.length; i++) {
            const wordRangesForPara = wordRangesForParas[i]
            if (wordRangesForPara === null) {
                paraTokens.push([]);
                continue;
            } else {
                const tokens: string[] = [];
                for (let j = 0; j < wordRangesForPara.items.length; j++) {
                    tokens.push(wordRangesForPara.items[j].text);
                }
                paraTokens.push(tokens);
            }
        }
    });

    return paraTokens;
}

async function getSelectionFromCanvas(): Promise<string[]> {
    const selection: string[] = ["", ""];

    await Word.run(async context => {
        const firstParagraph = context.document.getSelection().paragraphs.getFirst();
        const lastParagraph = context.document.getSelection().paragraphs.getLast();

        const curSelection = context.document.getSelection();

        const firstParaRange = firstParagraph.getRange(Word.RangeLocation.start);
        const curSelectionStart = curSelection.getRange(Word.RangeLocation.start);
        const firstParaUnselectedTextRange = firstParaRange.expandTo(curSelectionStart);

        const lastParaRange = lastParagraph.getRange(Word.RangeLocation.end);
        const curSelectionEnd = curSelection.getRange(Word.RangeLocation.end);
        const lastParaMergeRange = lastParaRange.expandTo(curSelectionEnd);

        context.load(curSelection, "text");
        context.load(firstParaUnselectedTextRange, "text");
        context.load(lastParaMergeRange, "text");

        await context.sync();

        selection[0] = curSelection.text.trim();
        selection[1] = firstParaUnselectedTextRange.text.trim();
        selection[2] = lastParaMergeRange.text.trim();
    });

    return selection;
}

async function insertContentControls(fieldSpanIds: string[], paraIdxs: number[], tokenRanges: number[][]): Promise<boolean> {
    await Word.run(async context => {
        const wordRangesForParas = await getWordRangesForParas(paraIdxs, context);
        const fieldRanges: Array<Word.Range | null> = [];

        // build ranges
        for (let i = 0; i < tokenRanges.length; i++) {
            const tokenRange = tokenRanges[i];
            const wordRangesForPara = wordRangesForParas[i];

            if (tokenRange.length === 0 || wordRangesForPara === null) {
                fieldRanges.push(null);
                continue;
            }

            let mergedRanges: Word.Range = wordRangesForPara.items[tokenRange[0]];

            for (let j = 1; j < tokenRange.length; j++) {
                mergedRanges = mergedRanges.expandTo(wordRangesForPara.items[tokenRange[j]]);
            }

            context.load(mergedRanges);
            fieldRanges.push(mergedRanges);
        }

        await context.sync();

        for (let fldCnt3 = 0; fldCnt3 < fieldSpanIds.length; ++fldCnt3) {
            //BucketTimer.bucket(1, "700-createCC");
            const spanId = fieldSpanIds[fldCnt3];
            const foundRange = fieldRanges[fldCnt3];
            if (foundRange === null) {
                continue;
            }

            //BucketTimer.bucket(1, "820-WORD-createCC");
            // if range is already in a content control, and if the content control has a tag === spanId, then don't create the content control - it already exists.
            let alreadyCreated = false;
            const parentContentControl = foundRange.parentContentControlOrNullObject;
            await context.sync();
            if (!parentContentControl.isNullObject) {
                context.load(parentContentControl, "tag");
                await context.sync();
                if (parentContentControl.tag === spanId) {
                    alreadyCreated = true;
                }
            }
            else {
                // Returned ranges from Range.getTextRanges (see getWordRangesForParas) are not always fully in or out of the content control.
                // So foundRange might not fully be in the content control, either its start might be before the content control opening tag
                // or its end might be past the content control closing tag. In those cases, parentContentControlOrNullObject returns a null 
                // object and we need to check content controls inside the range instead.
                const contentControls = foundRange.contentControls;
                context.load(contentControls, "items");
                await context.sync();
                for (const contentControl of contentControls.items) {
                    context.load(contentControl, "tag")
                    await context.sync();
                    if (contentControl.tag === spanId) {
                        alreadyCreated = true;
                        break;
                    }
                }
            }

            // TODO: #16000 Note that nothing here handles the case where the content control exists and has the correct tag, but does not match the range.
            // If this is a possible scenario, we should remove the existing control in that case (but keep the text), and then re-create a new control 
            // over the correct range.

            if (alreadyCreated) {
                Logger.LogDebug(LoggerCategory.AssistedAuthoring, `dgCreateContentControlsForFields content control already exists for spanId: ${spanId}`);
            }
            else {
                Logger.LogDebug(LoggerCategory.AssistedAuthoring, `dgCreateContentControlsForFields inserting content control for spanId: ${spanId}`);
                let cc: any;
                //BucketTimer.bucket(1, "820-WORD-createCC");
                cc = foundRange.insertContentControl();
                cc.tag = spanId;
                cc.getRange().font.highlightColor = "cfdbf0";
            }
        }
        await context.sync().catch(function (error) {
            Logger.LogDebug(LoggerCategory.AssistedAuthoring, "dgCreateContentControlsForFields: caught error")
            if (error instanceof OfficeExtension.Error) {
                Logger.LogDebug(LoggerCategory.AssistedAuthoring, 'dgCreateContentControlsForFields: Error code and message: ' + error.toString());
            }
        });
    });

    return true;
}

async function setEatEvents(eat: boolean): Promise<boolean> {
    eatEvents = eat;
    return true;
}

async function getEatEvents(): Promise<boolean> {
    return eatEvents;
}

async function getGuids(num: number): Promise<string[]> {
    const guids: string[] = [];
    for (let i = 0; i < num; i++) {
        guids.push(uuidv4())
    }
    return guids;
}

async function ack(msg: string): Promise<boolean> {
    Logger.LogDebug(LoggerCategory.AssistedAuthoring, "ack fg " + msg);
    return true;
}

function packageProxyCallbacks(): IDgWordCanvasProxyDirectProxies {
    return Comlink.proxy({
        getBodyOoxml: Comlink.proxy(getBodyOoxml),
        replaceBodyOoxml: Comlink.proxy(replaceBodyOoxml),
        selectOrDeleteContentByParaIdxsAndTokens: Comlink.proxy(selectOrDeleteContentByParaIdxsAndTokens),
        getTokenizedParagraphs: Comlink.proxy(getTokenizedParagraphs),
        getSelectionFromCanvas: Comlink.proxy(getSelectionFromCanvas),
        insertContentControls: Comlink.proxy(insertContentControls),
        setEatEvents: Comlink.proxy(setEatEvents),
        getEatEvents: Comlink.proxy(getEatEvents),
        getUuids: Comlink.proxy(getGuids),
        bucketTimer: Comlink.proxy(bucketTimer),
        ack: Comlink.proxy(ack)
    });
}

export class DgWordCanvasProxy {
    // public fields that persist as state
    public firstSelectionEventReceived: boolean = false;
    public dgWordCanvasWorker: Worker = new Worker();
    public initialized: boolean = false;
    public fieldSpanIdList: string[] = [];

    // public fields that persist as state
    public readonly onSelection = new DgEvent<DgOnSelectionEventArgs>();
    public readonly onFieldValueChange = new DgEvent<DgOnFieldValueChange>();
    public readonly onKnownChunksChange = new DgEvent<DgKnownChunk[]>();

    constructor() {
        Office.context.document.addHandlerAsync(Office.EventType.DocumentSelectionChanged, this.handler.bind(this));
    }

    public async initialize(dgml: string, fieldSpanIdList: string[]): Promise<boolean> {
        if (this.initialized) {
            Logger.LogError(LoggerCategory.AssistedAuthoring, "initialized called twice on proxy");
            return false;
        }

        await Word.run(async (context) => {
            context.document.changeTrackingMode = Word.ChangeTrackingMode.off;
            await context.sync();
        });

        this.fieldSpanIdList = [...fieldSpanIdList];
        this.initialized = await this.dgWordCanvasWorker.init(dgml, fieldSpanIdList);

        if (!this.initialized) {
            Logger.LogError(LoggerCategory.AssistedAuthoring, "proxy initialization failed");
        }

        return this.initialized;
    }

    private async handler(evtArgs: Office.DocumentSelectionChangedEventArgs) {
        await DgWordCanvasProxy.updateTaskPaneCards(this);
        DgWordCanvasProxy.processSelectEvent(this);
    }

    public static async updateTaskPaneCards(dgWordCanvasProxy: DgWordCanvasProxy)
    {
        if (!!contentChangedTimeoutToken) {
            clearTimeout(contentChangedTimeoutToken);
            contentChangedTimeoutToken = null;
        }

        contentChangedTimeoutToken = setTimeout(
            () => {
                contentChangedTimeoutToken = null;
                DgWordCanvasProxy.updateIfDebounceTimePassed(dgWordCanvasProxy);
            }, DOCUMENT_CHANGED_DEBOUNCE_MS);
    }

    public static async updateIfDebounceTimePassed(dgWordCanvasProxy: DgWordCanvasProxy)
    {
        if (lastDocumentContents === '')
        {
            let lastDocumentContentsText = await getBodyOoxml();
            let lastDocumentContentsXml = (new Ltxml.DOMParser()).parseFromString(lastDocumentContentsText, "text/xml");
            let localStringArray: string[] = [];
            this.getTextContent(lastDocumentContentsXml, localStringArray);
            lastDocumentContents = localStringArray.join('');
        }

        let currentDocumentContentsText: string = await getBodyOoxml();
        let currentDocumentContents = (new Ltxml.DOMParser()).parseFromString(currentDocumentContentsText, "text/xml");

        let stringArray: string[] = [];
        DgWordCanvasProxy.getTextContent(currentDocumentContents.documentElement, stringArray);
        let textContent = stringArray.join('');

        if (lastDocumentContents === textContent)
        {
            Logger.LogDebug(LoggerCategory.AssistedAuthoring, "updateTaskPaneCards document not changed, aborting");
            return;
        }
        lastDocumentContents = textContent;
        Logger.LogDebug(LoggerCategory.AssistedAuthoring, "updateTaskPaneCards document CHANGED");

        const dgKnownChunks = await dgWordCanvasProxy.getKnownChunks();
        if (!dgKnownChunks)
        {
            Logger.LogError(LoggerCategory.AssistedAuthoring, `getKnownChunks returned null`)
            return;
        }

        dgWordCanvasProxy.onKnownChunksChange.trigger(dgKnownChunks);
    }

    public static getTextContent(node: any, arrayOfStrings: string[])
    {
        if (node.nodeType) {
            if (node.nodeType === 1 && node.nodeName !== 'pkg:binaryData') {
                let cn = node.childNodes;
                for (let cnt3 = 0; cnt3 < cn.length; cnt3 += 1) {
                    DgWordCanvasProxy.getTextContent(cn[cnt3], arrayOfStrings);
                }
                return;
            }

            if (node.nodeType === 3) {
                arrayOfStrings.push(node.textContent);
                return;
            }

            return;
        }

        throw new Error("Internal Error");
    }

    public static async processSelectEvent(dgWordCanvasProxy: DgWordCanvasProxy) {
        if (!dgWordCanvasProxy.initialized) {
            Logger.LogDebug(LoggerCategory.AssistedAuthoring, 'processSelectEvent: called before initialization');
            return;
        }

        if (eatEvents) {
            Logger.LogDebug(LoggerCategory.AssistedAuthoring, 'processSelectEvent: eating selection event');
            setTimeout(() => eatEvents = false, 5000); // after 5 seconds, do not eat events, even if not reset elsewhere.
            return;
        }

        Logger.LogDebug(LoggerCategory.AssistedAuthoring, `processSelectEvent - select event received`);
        if (dgWordCanvasProxy.firstSelectionEventReceived === false) {
            dgWordCanvasProxy.firstSelectionEventReceived = true;
            // we do this only for OfficeOnline, due to the bogus first selection event received.
            if ((window as any)._AddInPlatform === "OfficeOnline") {
                return;
            }
        }
        await Word.run(async (context) => {
            const paragraph = context.document.getSelection().paragraphs.getFirst();

            const curSelection = context.document.getSelection();

            const curSelectionRange = curSelection.getRange(Word.RangeLocation.content);

            const paraRange = paragraph.getRange(Word.RangeLocation.start);
            const mergeRange = paraRange.expandTo(curSelection);

            context.load(curSelectionRange, "text");
            context.load(mergeRange, "text");

            await context.sync();

            const expandToAncestor = !mergeRange.text;
            const parentContentControl = curSelection.parentContentControlOrNullObject;
            context.load(parentContentControl);
            context.load(parentContentControl, "text");
            await context.sync();

            if (parentContentControl.tag) {
                Logger.LogDebug(LoggerCategory.AssistedAuthoring, `processSelectEvent - event was for content control`);
                const tag = parentContentControl.tag;
                const eventArgs: DgOnSelectionEventArgs = {
                    DgmlSpanId: tag,
                    ParagraphDgmlSpanId: null,
                    PreviousParagraphDgmlSpanId: tag,
                    IsField: true,
                    SelectionLength: curSelectionRange.text.length
                };
                dgWordCanvasProxy.onSelection.trigger(eventArgs);

                const valueEventArgs: DgOnFieldValueChange = {
                    DgmlSpanId: tag,
                    NewValue: parentContentControl.text,
                };
                dgWordCanvasProxy.onFieldValueChange.trigger(valueEventArgs);

                return;
            }

            // this is the temporary code, this sends selection events for the first chunk in the paragraph.

            const paraOoXml = paragraph.getOoxml();
            await context.sync();

            const rangeRoot = XElement.parse(paraOoXml.value);
            const paragraphUuid = getDocugamiIdFromRootElement(rangeRoot) as string;
            Logger.LogTrace(LoggerCategory.AssistedAuthoring, `processSelectEvent paragraphUuid ${paragraphUuid}`);

            // call the worker to find the information for the selected paragraph
            const proxies = packageProxyCallbacks();
            const eventArgs = await dgWordCanvasProxy.dgWordCanvasWorker.getSelectedParaInfo(paragraphUuid, expandToAncestor, proxies);
            if (eventArgs !== null) {
                eventArgs.SelectionLength = curSelectionRange.text.length;
                dgWordCanvasProxy.onSelection.trigger(eventArgs);
            }
        });
    }

    /**
     * Returns whether the canvas currently contains the specified chunk.
     * @param spanId The chunk ID to find.
     */
    public async isChunkOnCanvas(spanId: string): Promise<boolean> {
        if (!this.initialized) {
            Logger.LogError(LoggerCategory.AssistedAuthoring, "isChunkOnCanvas called prior to initialization")
            return false;
        }

        const proxies = packageProxyCallbacks();
        return await this.dgWordCanvasWorker.isChunkOnCanvas(spanId, proxies);
    }

    public async getKnownChunks(): Promise<DgKnownChunk[]|null> {
        if (!this.initialized) {
            Logger.LogError(LoggerCategory.AssistedAuthoring, "getKnownChunks called prior to initialization")
            return null;
        }

        const proxies = packageProxyCallbacks();
        return await this.dgWordCanvasWorker.getKnownChunks(proxies);
    }

    public async insertContent(precedingSiblingId: string, followingSiblingId: string, parentId: string, chunkSuggestionId: string, chunkXmlToInsert: string) {
        if (!this.initialized) {
            Logger.LogError(LoggerCategory.AssistedAuthoring, "insertContent called prior to initialization")
            return;
        }

        Logger.LogDebug(LoggerCategory.AssistedAuthoring, `DgWordCanvasProxy.insertContent chunkSuggestionId ${chunkSuggestionId}`);
        const proxies = packageProxyCallbacks();
        await this.dgWordCanvasWorker.insertContent(precedingSiblingId, followingSiblingId, parentId, chunkSuggestionId, chunkXmlToInsert, proxies);
    }

    public async insertContentAtSelection(chunkXmlToInsert: string)
    {
        if (!this.initialized)
        {
            Logger.LogError(LoggerCategory.AssistedAuthoring, "insertContentAtSelection called prior to initialization")
            return;
        }

        await Word.run(async context => {
            let curSelection = context.document.getSelection();
            const curSelectionContent = curSelection.getRange(Word.RangeLocation.content);
            context.load(curSelectionContent, "text");
            await context.sync();

            if (curSelectionContent.text.length !== 0)
            {
                curSelection.delete();
            }

            curSelection = context.document.getSelection();
            const curSelectionStart = curSelection.getRange(Word.RangeLocation.start);
            const curSelectionEnd = curSelection.getRange(Word.RangeLocation.end);
            await context.sync();

            const theParagraph = context.document.getSelection().paragraphs.getFirst();
            const theParagraphOoxml = theParagraph.getRange().getOoxml();
            await context.sync();

            const theParagraphXElement = XElement.parse(theParagraphOoxml.value);
            const pPr = theParagraphXElement.descendants(W.pPr).firstOrDefault();

            const paragraphStartRange = theParagraph.getRange(Word.RangeLocation.start);
            const beforeSelectionRange = paragraphStartRange.expandTo(curSelectionStart);
            context.load(beforeSelectionRange, "text");
            await context.sync();

            const paragraphEndRange = theParagraph.getRange(Word.RangeLocation.end);
            const afterSelectionRange = curSelectionEnd.expandTo(paragraphEndRange);
            context.load(afterSelectionRange, "text");
            await context.sync();

            const beforeSelectionOoxml = beforeSelectionRange.text.length > 0 ? beforeSelectionRange.getOoxml() : null;
            const afterSelectionOoxml = afterSelectionRange.text.length > 0 ? afterSelectionRange.getOoxml() : null;

            await context.sync();

            const beforeSelectionXElement = !!beforeSelectionOoxml ? XElement.parse(beforeSelectionOoxml.value) : null;

            let beforeSelectionPara: any = null;
            if (beforeSelectionXElement !== null)
            {
                beforeSelectionPara = beforeSelectionXElement
                    .descendants(W.p)
                    .firstOrDefault();
                DgWordCanvasProxy.updateOrAdd(beforeSelectionPara, pPr);
            }

            const afterSelectionXElement = !!afterSelectionOoxml ? XElement.parse(afterSelectionOoxml.value) : null;

            let afterSelectionPara: any = null;
            if (afterSelectionXElement !== null)
            {
                afterSelectionPara = afterSelectionXElement
                    .descendants(W.p)
                    .firstOrDefault();
                DgWordCanvasProxy.updateOrAdd(afterSelectionPara, pPr);
            }

            const newContentXElement = XElement.parse(theParagraphOoxml.value);

            const abstractXml = produceAbstractXml(chunkXmlToInsert);
            const transformedContentXElement = transformAbstractXmlToWml(newContentXElement, theParagraphXElement, beforeSelectionPara, abstractXml, afterSelectionPara);

            theParagraph.insertOoxml(transformedContentXElement.toString(), Word.InsertLocation.replace);
        });
    }

    private static updateOrAdd(para: any, pPr: any) {
        const existingPpr = para.descendants(W.pPr).firstOrDefault();
        if (existingPpr === null)
        {
            para.addFirst(pPr);
        }
        else
        {
            existingPpr.replaceWith(pPr);
        }
    }

    public async getWordCanvasSelectionAsDgml(): Promise<string|null>
    {
        if (!this.initialized) {
            Logger.LogError(LoggerCategory.AssistedAuthoring, "getWordCanvasSelectionAsDgml called prior to initialization")
            return null;
        }

        let curSelectionText: string = `<dg:chunk structure='p' xmlns:dg='http://www.docugami.com/2021/dgml'></dg:chunk>`;
        await Word.run(async context =>
        {
            let curSelection = context.document.getSelection();
            const curSelectionContent = curSelection.getRange(Word.RangeLocation.content);
            let OOXML: any = curSelectionContent.getOoxml();

            context.load(curSelectionContent);
            await context.sync();

            const xele = XElement.parse(OOXML.value);
            const dgml = transformWmlToDgml(xele);
            curSelectionText = dgml.toString();

            // console.log(`current selection dgml ${curSelectionText}`);
        });
        return curSelectionText;
    }

    public async replaceContent(spanIdToReplace: string, chunkXmlToInsert: string) {
        if (!this.initialized) {
            Logger.LogError(LoggerCategory.AssistedAuthoring, "replaceContent called prior to initialization")
            return;
        }

        const proxies = packageProxyCallbacks();
        await this.dgWordCanvasWorker.replaceContent(spanIdToReplace, chunkXmlToInsert, proxies);
    }

    public async deleteContent(spanIdToDelete: string) {
        if (!this.initialized) {
            Logger.LogError(LoggerCategory.AssistedAuthoring, "deleteContent called prior to initialization")
            return;
        }

        const proxies = packageProxyCallbacks();
        await this.dgWordCanvasWorker.deleteContent(spanIdToDelete, proxies);
    }

    public async createContentControlsForFields() {
        if (!this.initialized) {
            Logger.LogError(LoggerCategory.AssistedAuthoring, "deleteContent called prior to initialization")
            return;
        }

        const proxies = packageProxyCallbacks();
        await this.dgWordCanvasWorker.createContentControlsForFields(proxies);
    }

    public async selectParagraphContainingChunk(spanId: string) {
        if (!this.initialized) {
            Logger.LogError(LoggerCategory.AssistedAuthoring, "selectParagraphContainingChunk called prior to initialization")
            return;
        }

        const proxies = packageProxyCallbacks();
        await this.dgWordCanvasWorker.selectContent(spanId, proxies);
    }

    public async setSelectionIP(precedingSiblingId: string, followingSiblingId: string, parentId: string) {
        if (!this.initialized) {
            Logger.LogError(LoggerCategory.AssistedAuthoring, "setSelectionIP called prior to initialization")
            return;
        }

        const proxies = packageProxyCallbacks();
        await this.dgWordCanvasWorker.setSelectionIP(precedingSiblingId, followingSiblingId, parentId, proxies);
    }

    public async getFieldValues(): Promise<{ [spanId: string]: string }> {
        const valuesBySpanId: { [spanId: string]: string } = {};

        await Word.run(async context => {
            const contentControls = context.document.contentControls;

            context.load(contentControls);
            await context.sync();

            for (const cc of contentControls.items) {
                if (cc.tag) {
                    valuesBySpanId[cc.tag] = cc.text;
                }
            }
        });

        return valuesBySpanId;
    }

    public async setFieldValue(spanId: string, value: string): Promise<boolean> {
        let set: boolean = false;

        await Word.run(async context => {
            const contentControls = context.document.contentControls;

            context.load(contentControls);
            await context.sync();

            for (const cc of contentControls.items) {
                if (cc.tag === spanId) {
                    cc.insertText(value, Word.InsertLocation.replace);
                    await context.sync();
                    set = true;
                    break;
                }
            }
        });

        return set;
    }

    public async selectField(spanId: string): Promise<boolean> {
        let selected: boolean = false;

        await Word.run(async context => {
            const contentControls = context.document.contentControls;

            context.load(contentControls);
            await context.sync();

            for (const cc of contentControls.items) {
                if (cc.tag === spanId) {
                    cc.getRange().select();
                    await context.sync();
                    selected = true;
                    break;
                }
            }
        });

        return selected;
    }

    public async establishDgmlCorrelation() {
        if (this.dgWordCanvasWorker === null || !this.initialized) {
            Logger.LogError(LoggerCategory.AssistedAuthoring, "DgWordCanvas not initialized yet")
            return;
        }

        const proxies = packageProxyCallbacks();
        await this.dgWordCanvasWorker.establishDgmlCorrelation(proxies);
    }

    public async getContextSpanIds(spanId: string): Promise<string[]> {
        if (this.dgWordCanvasWorker === null || !this.initialized) {
            Logger.LogError(LoggerCategory.AssistedAuthoring, "DgWordCanvas not initialized yet")
            return [];
        }

        const proxies = packageProxyCallbacks();
        return await this.dgWordCanvasWorker.getContextSpanIds(spanId, proxies);
    }

    public async paintContentControls() {
        if (this.dgWordCanvasWorker === null || !this.initialized) {
            Logger.LogError(LoggerCategory.AssistedAuthoring, "DgWordCanvas not initialized yet")
            return [];
        }

        const proxies = packageProxyCallbacks();
        return await this.dgWordCanvasWorker.paintContentControls(proxies);
    }
};

